import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import KlydoModal from "./Klydos/components/KlydoModal";
import DSSwitch from "./system-design/DSSwitch";

import useKlydo from "../components/hooks/useKlydo";
import { isVideo, resizeCloudinary } from "../utils/util";
import DSDialog from "./system-design/DSDialog";
import DSBox from "./system-design/DSBox";
import DSTypography from "./system-design/DSTypography";
import DSIconButton from "./system-design/DSIconButton";
import DSCircularProgress from "./system-design/DSCircularProgress";
import { hasSound } from "../api/klydo";
interface KlydoListItemProps {
  klydoId: string;
  onRemove?: (klydoId: string) => void;
  canRemove?: boolean;
  handleSwitch?: (klydoId: string, checked: boolean) => void;
  switchValue?: boolean;
  switchTitle?: string;
}

const KlydoListItem: React.FC<KlydoListItemProps> = ({
  klydoId,
  onRemove,
  canRemove,
  handleSwitch,
  switchValue,
  switchTitle,
}) => {
  const [showKlydoModal, setShowKlydoModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { data: klydo, isLoading } = useKlydo(klydoId);

  const handleKlydoSelect: React.MouseEventHandler<HTMLDivElement> = (
    event,
  ) => {
    event.preventDefault();
    setShowKlydoModal(true);
  };

  const handleRemove = () => {
    setDeleting(true);
    if (onRemove && klydo?.id) onRemove(klydo.id);
    setDeleting(false);
  };

  return (
    <>
      {klydo && !isLoading && (
        <DSBox
          key={klydo?.id}
          onClick={handleKlydoSelect}
          sx={{
            px: 2,
            cursor: "pointer",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <DSBox
            sx={{
              fontSize: "13px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <DSBox title={klydo?.id} sx={{ minWidth: 150 }}>
              {klydo?.name}
            </DSBox>
            <DSBox title={klydo?.creator} sx={{ minWidth: 100 }}>
              {klydo?.author?.name}
            </DSBox>

            <DSBox sx={{ minWidth: 50, textAlign: "center" }}>
              <DSTypography
                sx={{
                  fontSize: "14px",
                  color: hasSound(klydo?.capabilitiesV2?.animationSound?.mode)
                    ? "#4caf50"
                    : "text.disabled",
                  fontWeight: 600,
                }}
              >
                {hasSound(klydo?.capabilitiesV2?.animationSound?.mode)
                  ? "✓"
                  : "✗"}
              </DSTypography>
            </DSBox>

            <DSBox sx={{ minWidth: 50, textAlign: "center" }}>
              <DSTypography
                sx={{
                  fontSize: "14px",
                  color:
                    klydo?.chimesUrls && klydo.chimesUrls.length > 0
                      ? "#4caf50"
                      : "text.disabled",
                  fontWeight: 600,
                }}
              >
                {klydo?.chimesUrls && klydo.chimesUrls.length > 0 ? "✓" : "✗"}
              </DSTypography>
            </DSBox>

            <DSBox sx={{ minWidth: 70, textAlign: "center" }}>
              <DSTypography
                sx={{
                  fontSize: "14px",
                  color: klydo?.metadata?.generatedByAI
                    ? "#2196f3"
                    : "text.disabled",
                  fontWeight: 600,
                }}
              >
                {klydo?.metadata?.generatedByAI ? "✓" : "✗"}
              </DSTypography>
            </DSBox>
            {!klydo?.loopUrl || isVideo(klydo?.loopUrl) === "none" ? (
              <DSBox
                sx={{
                  width: 35,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <DSTypography>--</DSTypography>
              </DSBox>
            ) : (
              <DSBox
                sx={{
                  borderRadius: "50%",
                  width: 35,
                  height: 35,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <DSBox
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    position: "absolute",
                    top: 0 + "%",
                    left: 0 + "%",
                    width: `${100}%`,
                    aspectRatio: "1/1",
                  }}
                >
                  {isVideo(klydo?.loopUrl) === "image" ? (
                    <img
                      loading="lazy"
                      alt="gif"
                      src={resizeCloudinary(klydo?.loopUrl, 48)}
                      style={{
                        width: "auto",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <video
                      muted={true}
                      src={resizeCloudinary(klydo?.loopUrl, 48)}
                      loop
                      autoPlay
                      style={{
                        width: "auto",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </DSBox>
              </DSBox>
            )}
            {handleSwitch && (
              <DSBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {switchTitle && (
                  <DSTypography sx={{ fontSize: "13px" }}>
                    {switchTitle}
                  </DSTypography>
                )}
                <DSSwitch
                  checked={!!switchValue}
                  onChange={(event) =>
                    handleSwitch(klydoId, event.target.checked)
                  }
                />
              </DSBox>
            )}
            {deleting ? (
              <DSCircularProgress size={20} />
            ) : (
              canRemove && (
                <DSIconButton
                  title="Remove klydo"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                >
                  <CloseIcon fontSize="small" />
                </DSIconButton>
              )
            )}
          </DSBox>
        </DSBox>
      )}

      {showKlydoModal && klydo && (
        <DSDialog
          fullWidth
          maxWidth="lg"
          open={showKlydoModal}
          onClose={() => setShowKlydoModal(false)}
        >
          <KlydoModal klydoId={klydo.id} setShowModal={setShowKlydoModal} />
        </DSDialog>
      )}
    </>
  );
};

export default KlydoListItem;
